import {
  $mobx,
  action,
  AnnotationsMap,
  CreateObservableOptions,
  isObservable,
  isObservableArray,
  isObservableObject,
  makeObservable,
} from 'mobx';

export function* result<T>(promise: Promise<T>) {
  return (yield promise) as T;
}

const annotationsSymbol = Symbol('annotationsSymbol');
const objectPrototype = Object.prototype;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare type NoInfer<T> = [T][T extends any ? 0 : never];

/**
 * A purposefully-limited version of `makeAutoObservable` that supports subclasses.
 *
 * There is valid complexity in supporting `makeAutoObservable` across disparate/edge-casey
 * class hierarchies, and so mobx doesn't support it out of the box. See:
 * https://github.com/mobxjs/mobx/discussions/2850#discussioncomment-1203102
 *
 * So this implementation adds a few limitations that lets us get away with it. Specifically:
 *
 * - We always auto-infer a key's action/computed/observable, and don't support user-provided config values
 * - Subclasses should not override parent class methods (although this might? work)
 * - Only the "most child" subclass should call `makeSimpleAutoObservable`, to avoid each constructor in
 *   the inheritance chain potentially re-decorating keys.
 *
 * See https://github.com/mobxjs/mobx/discussions/2850
 */
export function makeSimpleAutoObservable<
  T extends object,
  AdditionalKeys extends PropertyKey = never,
>(
  target: T,
  overrides?: AnnotationsMap<T, NoInfer<AdditionalKeys>>,
  options?: CreateObservableOptions,
): T {
  // Make sure nobody called makeObservable/etc. previously (eg in parent constructor)
  if (isObservable(target)) {
    throw new Error('Target must not be observable');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let annotations = (target as any)[annotationsSymbol];
  if (!annotations) {
    annotations = {};
    let current = target;
    while (current && current !== objectPrototype) {
      Reflect.ownKeys(current).forEach((key) => {
        if (key === $mobx || key === 'constructor') return;
        annotations[key] = !overrides
          ? true
          : key in overrides
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (overrides as any)[key]
          : true;
      });
      current = Object.getPrototypeOf(current);
    }
    // Cache if class
    const proto = Object.getPrototypeOf(target);
    if (proto && proto !== objectPrototype) {
      Object.defineProperty(proto, annotationsSymbol, { value: annotations });
    }
  }

  return makeObservable(target, annotations, options);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyObject = Record<string, any>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isObject(obj: any): obj is AnyObject {
  return obj && typeof obj === 'object' && !Array.isArray(obj);
}

/**
 * A utility function to update an observable object with a DTO, recursively updating nested objects
 * to allow for partial updates.
 *
 * This will result in less re-renders.
 */
export const smartUpdate = action(<T extends AnyObject>(current: T, dto: T): T => {
  for (const key of Object.keys(dto)) {
    const currentValue = current[key as keyof T];
    const newValue = dto[key as keyof T];

    if (isObject(currentValue) && isObject(newValue)) {
      if (isObservableObject(currentValue)) {
        // Recursively update observable nested objects
        smartUpdate(currentValue, newValue);
      } else {
        // Replace non-observable objects entirely
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (current as any)[key] = newValue;
      }
    } else if (isObservableArray(currentValue) && Array.isArray(newValue)) {
      // Handle observable arrays
      if (
        currentValue.length !== newValue.length ||
        !currentValue.every((val: any, index: number) => val === newValue[index])
      ) {
        // Recursively update objects inside the array
        for (let i = 0; i < newValue.length; i++) {
          if (isObject(newValue[i]) && isObject(currentValue[i])) {
            smartUpdate(currentValue[i], newValue[i]);
          } else {
            currentValue[i] = newValue[i];
          }
        }
        // If the new array is shorter, truncate the current array
        if (currentValue.length > newValue.length) {
          currentValue.splice(newValue.length);
        }
      }
    } else if (currentValue !== newValue) {
      // Update primitive values or references if changed
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (current as any)[key] = newValue;
    }
  }

  // Handle keys that are in `current` but not in `dto` (i.e., removed keys)
  for (const key of Object.keys(current)) {
    if (!(key in dto)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (current as any)[key];
    }
  }

  return current;
});
